// YERİ: src/app/domains/website/sections/mkt-services.section.ts
//
// HİZMETLER. Vitrin bugüne kadar Melbarrel'ın yalnız YARISINI anlatıyordu:
// 32 sektörel yazılım modülü. Oysa sistemde (Service tablosu) satılan asıl
// hizmet listesi çok daha geniş — marka kimliği, logo, web geliştirme, SEO,
// reklam yönetimi, sosyal medya, bakım. Yani Melbarrel bir SaaS DEĞİL,
// ajans + platform.
//
// Bu bölüm o boşluğu kapatır: her kategori bir pastel kart, içinde birkaç
// gerçek hizmet ve "şu fiyattan başlar" bilgisi. Kartlar slayt içinde durur —
// sekiz kategori alt alta yığılsaydı sayfa iki katına çıkardı.

import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SiteConfig, cfg } from '@/app/domains/site/sections/section.model';
import { LanguageStore } from '../core/language.store';
import { WebCmsService } from '../core/web-cms.service';
import { WebServiceCatalog } from '../core/web.models';
import { MktIcon } from './mkt-icon';
import { MktSlider } from './mkt-slider';
import { MktReveal } from './mkt-motion';
import { MktRouterLink, resolveMktLink } from './mkt-link';
import { PriceLabels, fillTokens, formatFromPrice, splitLines, DEFAULT_PRICE_LABELS } from './mkt-price';

/**
 * CMS'teki kategori ÇERÇEVESİ. `key` doluyken sayı, fiyat ve kalem adları
 * veritabanından gelir — CMS yalnız ikonu, başlığı ve spot cümleyi taşır.
 */
interface ServiceGroup {
  icon?: string;
  name?: string;
  description?: string;
  /** Service.Category karşılığı — kalemler bununla eşleşir. */
  key?: string;
  ctaText?: string;
  ctaUrl?: string;

  /**
   * source !== 'services' iken kullanılan ESKİ elle yazılmış alanlar. Yeni
   * içerikte bulunmazlar; 2026-08-08 migration'ı temizledi. Geriye dönük
   * uyumluluk için okunmaya devam ediyor (eski bir sayfa boş kart çizmesin).
   */
  count?: string;
  items?: string;
  priceFrom?: string;
}

/** Kartın çizime hazır hâli — kaynağı ne olursa olsun şablon bunu görür. */
interface RenderGroup {
  icon?: string;
  name?: string;
  description?: string;
  items: string[];
  count: string;
  priceFrom: string;
  ctaText?: string;
  ctaUrl?: string;
}

@Component({
  selector: 'mkt-services',
  standalone: true,
  imports: [RouterLink, MktIcon, MktSlider, MktReveal],
  host: { class: 'block border-y border-[color:var(--mkt-border)] bg-[color:var(--mkt-paper-soft)]' },
  template: `
    <div class="mx-auto max-w-7xl px-5 py-24 sm:px-8">

      <div class="max-w-3xl" mktReveal>
        @if (eyebrow()) {
          <p class="mkt-eyebrow">{{ eyebrow() }}</p>
        }
        <h2 class="mkt-h2 mt-4 text-3xl sm:text-[2.75rem]/[1.18]">{{ title() }}</h2>
        @if (subtitle()) {
          <p class="mkt-lead mt-5">{{ subtitle() }}</p>
        }
      </div>

      <div class="mt-14" mktReveal>
        <mkt-slider [count]="pages()">
          @for (page of grouped(); track $index) {
            <!-- Bir slaytta ÜÇ kategori: sekiz kategoriyi tek tek kaydırmak
                 sabır ister; üçerli gruplar hem hızlı taranır hem dolu görünür. -->
            <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              @for (group of page; track group.name; let i = $index) {
                <!-- SADE KART. Önceki hâlinde pastel gradyan dolgu, beyaz ikon
                     karosu, renkli "12 SERVICES" rozeti, her satırda tik işareti
                     ve bir buton vardı — yedi ayrı görsel öğe dikkat için
                     yarışıyordu ve kart "şablon" gibi duruyordu.
                     Şimdi: beyaz zemin + saç teli kenarlık, tek renkli öğe ikon.
                     Tik işaretleri kaldırıldı; bunlar bir "dahil özellikler"
                     listesi değil, satılan hizmetlerin adları — tik onları
                     pakete dahil gibi gösteriyordu. -->
                <article class="mkt-card mkt-card-hover group/card flex flex-col p-7">
                  <div class="flex items-center gap-3">
                    <span class="grid size-10 shrink-0 place-items-center rounded-xl bg-[color:var(--mkt-accent-soft)] text-[color:var(--mkt-accent)]">
                      <mkt-icon [name]="group.icon || 'sparkles'" [size]="19" />
                    </span>
                    <h3 class="mkt-h3 text-lg">{{ group.name }}</h3>
                  </div>

                  @if (group.description) {
                    <p class="mt-4 text-[0.9375rem] leading-relaxed text-[color:var(--mkt-text-muted)]">
                      {{ group.description }}
                    </p>
                  }

                  @if (group.items.length) {
                    <!-- Hizmet adları: işaretsiz, ince ayıraçlı satırlar.
                         Bir katalog listesi böyle okunur; süslemesi olmayan
                         liste daha güvenilir durur. -->
                    <ul class="mt-6 flex-1 divide-y divide-[color:var(--mkt-border)] border-y border-[color:var(--mkt-border)]">
                      @for (item of group.items; track $index) {
                        <li class="py-2.5 text-[0.875rem] text-[color:var(--mkt-text-muted)]">{{ item }}</li>
                      }
                    </ul>
                  }

                  <div class="mt-6 flex items-baseline justify-between gap-3">
                    @if (group.priceFrom) {
                      <!-- Fiyat tek satır: "from" küçük ve sönük, tutar kalın.
                           İki satırlık büyük harfli etiket kartı ağırlaştırıyordu. -->
                      <span class="text-[0.9375rem]">
                        <span class="text-[color:var(--mkt-text-faint)]">{{ priceLabel() }}</span>
                        <span class="ml-1.5 font-bold tracking-tight">{{ group.priceFrom }}</span>
                      </span>
                    }
                    @if (group.count) {
                      <span class="text-[0.8125rem] text-[color:var(--mkt-text-faint)]">{{ group.count }}</span>
                    }
                  </div>

                  @if (group.ctaText) {
                    <a [routerLink]="link(group.ctaUrl)"
                      class="mkt-link mt-5 !text-[0.875rem]">
                      {{ group.ctaText }}
                      <mkt-icon name="arrow" [size]="14" />
                    </a>
                  }
                </article>
              }
            </div>
          }
        </mkt-slider>
      </div>

      @if (footnote()) {
        <p class="mt-10 text-center text-sm text-[color:var(--mkt-text-faint)]">{{ footnote() }}</p>
      }
    </div>
  `,
})
export class MktServicesSection {
  readonly config = input.required<SiteConfig>();

  private langStore = inject(LanguageStore);
  private cms = inject(WebCmsService);

  protected eyebrow  = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title    = computed(() => cfg(this.config(), 'title', ''));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected priceLabel = computed(() => cfg(this.config(), 'priceLabel', 'from'));

  /**
   * Kalem/sayı/fiyat kaynağı: 'services' → veritabanı, başka her şey → CMS.
   *
   * Varsayılan 'services'. `mkt-service-catalog` ile aynı gerekçe, bir sayfa
   * ötesinde tekrarlanmıştı: bu kartlardaki "12 services" ve "from $50"
   * satırları elle yazılıydı ve 2026-08-07 fiyat revizyonundan sonra baştan
   * sona yanlıştı — hâlâ Google/Meta reklam yönetimi satıyor gösteriyordu.
   */
  protected source = computed(() => cfg<string>(this.config(), 'source', 'services'));

  /** Kartta gösterilecek en fazla kalem adı — katalog listesi /services'te. */
  protected maxItems = computed(() => {
    const raw = Number(cfg<string>(this.config(), 'maxItems', '6'));
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 6;
  });

  private catalog = signal<WebServiceCatalog | null>(null);

  constructor() {
    effect(() => {
      if (this.source() !== 'services') return;

      const lang = this.langStore.current();
      this.cms.getServiceCatalog(lang).subscribe({
        next: (data) => this.catalog.set(data),
        error: () => this.catalog.set(null),
      });
    });
  }

  /** Birim sözcükleri CMS'ten; tutarın kendisi veriden. */
  private labels = computed<PriceLabels>(() => ({
    from: this.priceLabel(),
    month: cfg(this.config(), 'unitMonth', DEFAULT_PRICE_LABELS.month),
    hour: cfg(this.config(), 'unitHour', DEFAULT_PRICE_LABELS.hour),
    page: cfg(this.config(), 'unitPage', DEFAULT_PRICE_LABELS.page),
    clip: cfg(this.config(), 'unitClip', DEFAULT_PRICE_LABELS.clip),
    language: cfg(this.config(), 'unitLanguage', DEFAULT_PRICE_LABELS.language),
    included: cfg(this.config(), 'includedText', DEFAULT_PRICE_LABELS.included),
  }));

  private frames = computed<ServiceGroup[]>(() => {
    const raw = this.config()['groups'];
    return Array.isArray(raw) ? raw : [];
  });

  /**
   * Dipnot da veriye bağlı: `{currency}` / `{total}` / `{modules}` yer tutucuları
   * doldurulur. Para birimini elle yazmak bir dönem "prices in USD" yalanını
   * üretmişti — oysa bütün satırlar CAD ve USD hiç yayında değil.
   */
  protected footnote = computed(() => {
    const data = this.catalog();
    return fillTokens(cfg(this.config(), 'footnote', ''), {
      currency: data?.currency ?? '',
      total: data?.totalCount ?? '',
      modules: data?.moduleCount ?? '',
    });
  });

  /** CMS çerçevesi + DB'den gelen sayı, fiyat ve kalem adları. */
  protected groups = computed<RenderGroup[]>(() => {
    const frames = this.frames();

    if (this.source() !== 'services') {
      return frames.map((f) => ({
        icon: f.icon,
        name: f.name,
        description: f.description,
        items: splitLines(f.items),
        count: f.count ?? '',
        priceFrom: f.priceFrom ?? '',
        ctaText: f.ctaText,
        ctaUrl: f.ctaUrl,
      }));
    }

    // Katalog gelmediyse HİÇBİR kart çizilmez. Bir dönem CMS'teki listeye
    // düşülüyordu; uç her düştüğünde satılamayan hizmetleri reklam etmek
    // anlamına geliyordu. Eksik kart, yanlış karttan iyidir.
    const data = this.catalog();
    if (!data) return [];

    const locale = this.langStore.current();
    const labels = this.labels();
    const byKey = new Map(data.categories.map((c) => [c.key, c]));
    const countTemplate = cfg(this.config(), 'countLabel', '{count} services');

    return frames
      .map((frame): RenderGroup | null => {
        const category = frame.key ? byKey.get(frame.key) : undefined;
        if (!category) return null;

        return {
          icon: frame.icon,
          name: frame.name,
          description: frame.description,
          items: category.items.slice(0, this.maxItems()).map((i) => i.name),
          count: fillTokens(countTemplate, { count: category.itemCount }),
          priceFrom: formatFromPrice(category, data.currency, locale, labels),
          ctaText: frame.ctaText,
          ctaUrl: frame.ctaUrl,
        };
      })
      // Kategorinin tüm hizmetleri pasifleştirilmişse kart hiç çizilmez.
      .filter((g): g is RenderGroup => g !== null);
  });

  /** Slayt başına üç kategori. */
  protected grouped = computed<RenderGroup[][]>(() => {
    const all = this.groups();
    const pages: RenderGroup[][] = [];

    for (let i = 0; i < all.length; i += 3) {
      pages.push(all.slice(i, i + 3));
    }
    return pages;
  });

  protected pages = computed(() => this.grouped().length);

  protected link(url?: string): MktRouterLink {
    return resolveMktLink(url ?? '', this.langStore.current());
  }
}

